<template>
  <form-layout
    class="scope-of-work-form"
    :loading="isLoading"
    @save="saveScopeOfWork"
  >
    <template #title>
      <h1 class="uppercase">Add Scope of Work</h1>
    </template>
    <template #body>
      <bdg-name ref="title" name="Title" v-model="formData.name" required />

      <bdg-select-tabs
        name="Scope Type"
        :options="formattedDocumentTypes"
        v-model="formData.type"
        required
      />

      <bdg-radios-group
        ref="location"
        name="Location of project"
        :options="locationOptions"
        v-model="formData.location"
        required
      />

      <bdg-radios-group
        ref="projectType"
        name="Type of project"
        :options="types"
        v-model="formData.project_type"
        required
      />

      <template v-if="isDesign">
        <bdg-checkboxes-group
          ref="designSheets"
          name="Required design sheets"
          :options="designSheets"
          v-model="formData.design_sheets_ids"
          key="Design Sheets"
          required
        />

        <bdg-checkboxes-group
          ref="additionalServices"
          name="Additional services"
          :options="additionalServices"
          v-model="formData.additional_services_ids"
          key="Additional Services"
          required
        />
      </template>
      <template v-else-if="isConstruction">
        <bdg-checkboxes-group
          ref="scopeItems"
          name="Required scope items"
          :options="scopeItems"
          v-model="formData.scope_items_ids"
          key="Scope Items"
          required
        />
      </template>
      <template v-else-if="isChangeOrder">
        <bdg-checkboxes-group
          ref="changeOrder"
          name="Required scope items"
          :options="changeOrder"
          v-model="formData.change_order_items_ids"
          key="Change Orders"
          required
        />
      </template>
    </template>
  </form-layout>
</template>

<script>
import FormLayout from "../../layouts/FormLayout";
import BdgSelectTabs from "../../../forms/fields/BdgSelectTabs";
import {
  Locations,
  ScopeDocumentCondensedTypes,
  ScopeDocumentTypes,
  Types
} from "../../../constants/contract-enums";
import BdgName from "../../../forms/fields/BdgTextField";
import BdgRadiosGroup from "../../../forms/fields/BdgRadiosGroup";
import BdgCheckboxesGroup from "../../../forms/fields/BdgCheckboxesGroup";
import { validateRequiredRefs } from "../../../helpers";

export default {
  name: "ScopeOfWorkForm",
  components: {
    BdgName,
    BdgCheckboxesGroup,
    BdgRadiosGroup,
    BdgSelectTabs,
    FormLayout
  },
  props: {
    id: Number,
    contract_id: Number,
    name: String,
    type: {
      type: String,
      default: ScopeDocumentCondensedTypes.DESIGN
    },
    location: {
      type: String,
      default: Locations.FRONT
    },
    project_type: {
      type: String,
      default: Types.NEW
    },
    design_sheets_ids: {
      type: Array,
      default: () => []
    },
    change_orders_ids: {
      type: Array,
      default: () => []
    },
    scope_items_ids: {
      type: Array,
      default: () => []
    },
    additional_services_ids: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      documentTypes: Object.values(ScopeDocumentCondensedTypes),
      locationOptions: Object.values(Locations),
      types: Object.values(Types),
      formData: {
        id: this.id,
        name: this.name,
        contract_id: this.contract_id || this.$route.params.id,
        type: this.type,
        location: this.location,
        project_type: this.project_type,
        design_sheets_ids: this.design_sheets_ids,
        change_order_items_ids: this.change_orders_ids,
        scope_items_ids: this.scope_items_ids,
        additional_services_ids: this.additional_services_ids
      },
      windowWidth: window.innerWidth
    };
  },
  computed: {
    isDesign() {
      return this.formData.type === ScopeDocumentCondensedTypes.DESIGN;
    },
    isConstruction() {
      return (
        this.formData.type === ScopeDocumentCondensedTypes.CONSTRUCTION ||
        this.formData.type === "const."
      );
    },
    isChangeOrder() {
      return this.formData.type === ScopeDocumentCondensedTypes.CHANGE_ORDER;
    },
    formattedDocumentTypes() {
      if (this.windowWidth >= 1700) {
        return this.documentTypes;
      }

      return this.documentTypes.map(type => {
        if (type === ScopeDocumentCondensedTypes.CONSTRUCTION) {
          return "const.";
        }
        return type;
      });
    },
    requiredRefs() {
      let requiredRefs = ["title", "location", "projectType"];
      if (this.isDesign) {
        requiredRefs.push("designSheets", "additionalServices");
      } else if (this.isConstruction) {
        requiredRefs.push("scopeItems");
      } else {
        requiredRefs.push("changeOrder");
      }
      return requiredRefs;
    },
    allScopeItems() {
      return this.$store.state.Settings.allScopeItems;
    },
    designSheets() {
      if (this.allScopeItems) {
        return this.filterItemsByType(
          this.allScopeItems,
          ScopeDocumentTypes.DESIGN_SHEETS
        );
      }
      return [];
    },
    additionalServices() {
      if (this.allScopeItems) {
        return this.filterItemsByType(
          this.allScopeItems,
          ScopeDocumentTypes.DESIGN_ADDITIONAL
        );
      }
      return [];
    },
    changeOrder() {
      if (this.allScopeItems) {
        return this.filterItemsByType(
          this.allScopeItems,
          ScopeDocumentTypes.CHANGE_ORDER
        );
      }
      return [];
    },
    scopeItems() {
      if (this.allScopeItems) {
        return this.filterItemsByType(
          this.allScopeItems,
          ScopeDocumentTypes.CONSTRUCTION
        );
      }
      return [];
    },
    allSelectedItemsIds() {
      return [
        ...this.formData.design_sheets_ids,
        ...this.formData.scope_items_ids,
        ...this.formData.additional_services_ids,
        ...this.formData.change_order_items_ids
      ];
    },
    isStoringScope() {
      return this.$store.state.Contracts.ScopeOfWork.isStoringScope;
    },
    isUpdatingScope() {
      return this.$store.state.Contracts.ScopeOfWork.isUpdatingScope;
    },
    isLoading() {
      return this.id ? this.isUpdatingScope : this.isStoringScope;
    }
  },
  created() {
    this.$store.dispatch("getAllScopeItems");
  },
  mounted() {
    window.onresize = () => {
      this.windowWidth = window.innerWidth;
    };
  },
  methods: {
    filterItemsByType(allItems, type) {
      return allItems
        .filter(i => i.type === type)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(i => {
          return { label: i.name, value: i.id };
        });
    },
    validate() {
      return validateRequiredRefs(this, true);
    },
    saveScopeOfWork() {
      if (!this.validate()) {
        return;
      }

      if (this.formData.type === "const.") {
        this.formData.type = ScopeDocumentCondensedTypes.CONSTRUCTION;
      }
      this.formData.work_scope_items = this.allSelectedItemsIds;
      if (this.id) {
        this.$store.dispatch("updateScope", this.formData);
      } else {
        this.$store.dispatch("storeScope", this.formData);
      }
    }
  }
};
</script>
